import { TabStyles } from '@tableau/widgets';
import { CSSProperties } from 'react';
import { media,style } from 'typestyle';
import { NestedCSSProperties } from 'typestyle/lib/types';

import { CommonStyles } from '~common/react/CommonStyles';

import { BreakPoints } from '../../../starters/styles/StarterStyles';
import { cssClass, Style } from '../../../style';
import { SpacesCommonStyles } from '../spaceComponents/SpacesStyles';

export const EDITABLE_NAME_ROW_HEIGHT = 42;

const cssPrefix = 'SpaceHeaderStyles';

function buildCss(className: string, ...properties: Style[]): string {
  return cssClass(cssPrefix, className, ...properties);
}

const row: NestedCSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  $nest: {
    '&>*': {
      marginRight: TabStyles.Spacing.MediumSpace
    },
    '&>*:last-child' : {
      marginRight: 0
    }
  }
};

const column: NestedCSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  $nest: {
    '&>*': {
      marginBottom: TabStyles.Spacing.DefaultSpace
    }
  }
};

export const truncated: React.CSSProperties = {
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  minWidth: 0
};

const center: NestedCSSProperties = {
  alignItems: 'center'
};

const MetadataStyles = {
  color: CommonStyles.Colors.Grey102,
  marginRight: TabStyles.Spacing.DefaultSpace
};

export namespace SpaceHeaderStyles {
  export const Row  = buildCss('row', row);
  export const RowAlignedCenter = buildCss('row_center', row, center, truncated);
  export const Column = buildCss('column', column, truncated);

  export const ColumnFlexGrow = cssClass(cssPrefix, 'row-column-grow', {
    ...column,
    ...truncated,
    flexGrow: 1
  });

  const MastheadIconWidthPx = '60px';
  export const IconDivWrapper = cssClass(cssPrefix, 'icon-div-wrapper', {
    minWidth: MastheadIconWidthPx,
    height: '60px',
    position: 'relative'
  });

  const IconStyle: React.CSSProperties = {
    height: '60px',
    width: MastheadIconWidthPx,
    position: 'absolute',
    // This vertically aligns it with the owner text
    top: '-5px',
    left: 0,
  };

  export const Icon = cssClass(cssPrefix, 'icon', {
    ...IconStyle
  });

  export const CollectionIcon = cssClass(cssPrefix, 'icon', {
    ...IconStyle,
    top: '0px',
  });

  export const SiteUserIcon = cssClass(cssPrefix, 'site-usericon', {
    height: '60px',
    width: MastheadIconWidthPx,
    position: 'absolute',
    // This vertically aligns it with the owner text
    top: '-5px',
    left: 0,
    borderRadius: '50%',
    objectFit: 'cover'
  });

  export const TitleCSS: React.CSSProperties = {
    fontWeight: 'normal',
    fontSize: SpacesCommonStyles.TitleFontSize,
    margin: '0',
    marginRight: TabStyles.Spacing.ExtraLargeSpace,
    height: SpacesCommonStyles.RowHeight,
    lineHeight: SpacesCommonStyles.RowHeight,
  };
  export const Title = buildCss('title', truncated, TitleCSS);

  const AlignMastheadEditableComponents: React.CSSProperties = {
    position: 'relative',
    right: CommonStyles.Sizes.DefaultSpace,
  };

  export const EditableHeaderStyles = cssClass(cssPrefix, 'editable-header', {
    overflow: 'visible',
    $nest: {
      '&>*': {
        marginBottom: 0,
        overflow: 'inherit',
        alignItems: 'flex-start',
      }
    }
  });

  export const EditableNameRow = cssClass(cssPrefix, 'editable-name-row', {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    flexGrow: 1,
    height: EDITABLE_NAME_ROW_HEIGHT,
    $nest: {
      '&>*': {
        marginRight: TabStyles.Spacing.MediumSpace
      },
      '&>*:last-child': {
        marginRight: 0
      }
    }
  });

  export const EditableTitleCSS: React.CSSProperties = {
    ...TitleCSS,
    marginRight: '0'
  };

  export const EditableTitleEditContainer: string = style(
    {
      ...AlignMastheadEditableComponents,
    },
    media({ maxWidth: BreakPoints.SMALL }, { minWidth: '325px' }),
    media({ minWidth: BreakPoints.SMALL + 1, maxWidth: BreakPoints.MEDIUM }, { minWidth: '650px' }),
    media({ minWidth: BreakPoints.MEDIUM + 1 }, { minWidth: '700px' }),
  );

  export const EditableTitleInputOverrides: React.CSSProperties = {
    ...EditableTitleCSS,
    height: EDITABLE_NAME_ROW_HEIGHT,
    width: '100%',
  };

  export const EditableTitleInput = cssClass(cssPrefix, 'editable-title-input', {
    $nest: {
      '& *': {
        marginBottom: 0,
      }
    },
    display: 'flex !important', // override widget styling
  });

  export const EditableTitleReadonly = buildCss('editable-title-readonly', truncated, EditableTitleCSS, {
    padding: CommonStyles.Sizes.DefaultSpace,
    ...AlignMastheadEditableComponents,
  });

  export const CollectionTitle = cssClass(cssPrefix, 'collection-title', {
    ...AlignMastheadEditableComponents,
    $nest: {
      '& button': {
        marginLeft: CommonStyles.Sizes.MediumSpace,
      },
    },
  });

  export const HeaderEditableInlineElement = cssClass(cssPrefix, 'header-editable-inline-element', {
    $nest: {
      '& button': {
        opacity: 0,
      },
      '&:hover button': {
        opacity: 1,
      },
      '& button:focus': {
        opacity: 1,
      },
      '&:hover': {
        background: 'rgb(245,245,245)',
        cursor: 'pointer',
      }
    }
  });

  const HeaderInlineDescription: NestedCSSProperties = {
    fontSize: 14,
    lineHeight: '21px',
  };

  const inlineDescriptionSmall = 350;
  const inlineDescriptionMedium = 650;
  const inlineDescriptionBig = 700;
  const editButtonWidth = 20 + CommonStyles.Sizes.SmallSpace;

  export const InlineDescriptionReadonly = style(
    {
      whiteSpace: 'normal',
      padding: CommonStyles.Sizes.DefaultSpace,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      wordBreak: 'break-word',
      ...HeaderInlineDescription,
      ...AlignMastheadEditableComponents,
    },
    media({ maxWidth: BreakPoints.SMALL }, { maxWidth: inlineDescriptionSmall + editButtonWidth }),
    media({ minWidth: BreakPoints.SMALL + 1, maxWidth: BreakPoints.MEDIUM }, { maxWidth: inlineDescriptionMedium + editButtonWidth }),
    media({ minWidth: BreakPoints.MEDIUM + 1 }, { maxWidth: inlineDescriptionBig + editButtonWidth }),
  );

  export const InlineDescriptionEditResponsiveContainer = style(
    {
      display: 'block',
      ...AlignMastheadEditableComponents,
    },
    media({ maxWidth: BreakPoints.SMALL }, { maxWidth: inlineDescriptionSmall }),
    media({ minWidth: BreakPoints.SMALL + 1, maxWidth: BreakPoints.MEDIUM }, { maxWidth: inlineDescriptionMedium }),
    media({ minWidth: BreakPoints.MEDIUM + 1 }, { maxWidth: inlineDescriptionBig }),
  );

  export const HeaderInlineDescriptionTextArea = cssClass(cssPrefix, 'inline-description-text-area', {
    width: '100%',
    $nest: {
      '& textarea': {
        minHeight: 75,
        resize: 'none',
        ...HeaderInlineDescription,
      }
    }
  });

  export const ConfirmCancelButtons = cssClass(cssPrefix, 'confirm-cancel', {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    marginTop: CommonStyles.Sizes.SmallSpace,
    $nest: {
      '& > *': {
        marginLeft: CommonStyles.Sizes.MediumSpace
      }
    }
  });

  export const Metadata = cssClass(cssPrefix, 'metadata', {
    fontWeight: 'normal',
    marginTop: TabStyles.Spacing.SmallSpace,
    alignItems: 'center',
    display: 'inline-flex',
    verticalAlign: 'middle',
    justifyContent: 'center',
    flexDirection: 'row',
    fontSize: SpacesCommonStyles.SubtitleFontSize,
  });

  export const TextLabelWithNoLeftMargin = cssClass(cssPrefix, 'text-label-no-left-padding',
    {...MetadataStyles});

  export const UserLabel = cssClass(cssPrefix, 'userlabel',
    {...MetadataStyles});

  export const ScheduleLabel = cssClass(cssPrefix, 'schedule-label',
    {...MetadataStyles});

  export const GroupLabel = cssClass(cssPrefix, 'group-label',
    {...MetadataStyles});

  export const headerLinkStyle: React.CSSProperties = {
    fontWeight: 'bold',
  };

  export const headerLink = cssClass(cssPrefix, 'headerLink', headerLinkStyle);

  export const userEmailHeaderLink = cssClass(cssPrefix, 'user-email-header-link', {
    ...headerLinkStyle,
    marginLeft: TabStyles.Spacing.ExtraLargeSpace
  });

  const encryptionStatusNest: any = {};
  encryptionStatusNest[`& .${headerLink}`] = {
    textDecoration: 'none'
  };
  encryptionStatusNest[`& .disabled`] = {
    fontWeight: 'normal',
    color: CommonStyles.Colors.Grey102
  };

  export const encryptionStatus = cssClass(cssPrefix, 'encryptionStatus', {
    display: 'inline-flex',
    marginLeft: TabStyles.Spacing.ExtraLargeSpace,
    marginRight: TabStyles.Spacing.DefaultSpace,
    $nest: encryptionStatusNest
  });

  export const ExtractStatus = cssClass(cssPrefix, 'extractStatus', {
    marginLeft: TabStyles.Spacing.ExtraLargeSpace,
    marginRight: TabStyles.Spacing.DefaultSpace,
  });

  export const ExtractStatusLabel = cssClass(cssPrefix, 'extractStatusLabel', {
    display: 'inline-block',
    marginLeft: TabStyles.Spacing.ExtraLargeSpace,
    marginRight: TabStyles.Spacing.DefaultSpace,
  });

  export const ExtractStatusSelection = cssClass(cssPrefix, 'extractStatusSelection', {
    display: 'inline-block',
    $nest: {
      '&>img': {
        position: 'fixed',
      }
    }
  });

  export const ExtractStatusSpinner = cssClass(cssPrefix, 'extractStatusSpinner', {
    display: 'inline-block',
    verticalAlign: 'sub',
    marginLeft: TabStyles.Spacing.DefaultSpace,
    marginRight: TabStyles.Spacing.DefaultSpace,
  });

  export const ExtractStatusText = cssClass(cssPrefix, 'extractStatusText', {
    display: 'inline-block',
    marginLeft: TabStyles.Spacing.DefaultSpace,
    marginRight: TabStyles.Spacing.DefaultSpace,
    $nest: encryptionStatusNest
  });

  export const DataLabel = cssClass(cssPrefix, 'datalabel',
    {...MetadataStyles,
      marginLeft: TabStyles.Spacing.ExtraLargeSpace,});

  export const DataStatusIcon = cssClass(cssPrefix, 'dataStatusIcon', {
        display: 'flex',
  });

  export const TextOnlyMetadataLabelWithLeftMargin = cssClass(cssPrefix, 'textOnlyLabelWithLeftMargin', {
    ...MetadataStyles,
    marginLeft: TabStyles.Spacing.ExtraLargeSpace
  });

  export const MetadataInfo = cssClass(cssPrefix, 'metadatainfo', {
    ...MetadataStyles,
    fontWeight: 'bold',
  });

  export const CertificationIcon = cssClass(cssPrefix, 'icon', {
    marginLeft: TabStyles.Spacing.ExtraLargeSpace,
    height: '16px',
    width: '16px',
    position: 'relative',
    left: 0,
    top: '3px'
  });

  export const HeaderIcon = cssClass(cssPrefix, 'headerIcon', {
    marginLeft: TabStyles.Spacing.ExtraLargeSpace,
    height: '16px',
    width: '16px',
    position: 'relative',
    left: 0,
  });

  export const TextAndIconMetadataLabel = cssClass(cssPrefix, 'textAndIconMetadataLabel', {
    marginLeft: TabStyles.Spacing.SmallSpace,
    ...MetadataStyles,
  });

  export const ItemDescription = cssClass(cssPrefix, 'description', {
    fontWeight: 'normal',
    fontSize: TabStyles.Typography.Sizing.Default,
    color: TabStyles.Typography.Color.PrimaryLight,
    overflow: 'hidden',
    position: 'relative',
    maxHeight: 35,
    textOverflow: 'ellipsis',
    whiteSpace: 'normal'
  });

  export const FavoriteTooltip = cssClass(cssPrefix, 'favoriteTooltip', {
    fontSize: SpacesCommonStyles.SubtitleFontSize,
    textAlign: 'center'
  });

  export const SpotlightActionButtonsRow = cssClass(cssPrefix, 'spotlightActionsRow', {
    marginTop: TabStyles.Spacing.MediumSpace
  });

  export const IconButtonStyleOverrides: CSSProperties = {
    minWidth: 0,
    height: 24,
    width: 24,
    padding: 0,
  };

  export const InfoDeprecatedContent = cssClass(cssPrefix, 'InfoDeprecatedContent', {
    backgroundColor: 'tableau-grey-06',
    paddingRight: TabStyles.Spacing.SmallSpace,
    paddingLeft: TabStyles.Spacing.SmallSpace,
  });

  export const InfoDetailImage = cssClass(cssPrefix, 'InfoDetailImage', {
    width: 18,
    height: 18,
    verticalAlign: 'middle',
  });
  export const HeaderButtonImage = cssClass(cssPrefix, 'HeaderButtonImage', {
    width: 18,
    height: 18,
    verticalAlign: 'middle',
  });
}
